interface GeminiAPIResponse {
  candidates: Array<{
    content: {
      parts: Array<{
        text: string;
      }>;
    };
    finishReason: string;
    safetyRatings: Array<{
      category: string;
      probability: string;
    }>;
  }>;
  usageMetadata: {
    promptTokenCount: number;
    candidatesTokenCount: number;
    totalTokenCount: number;
  };
}

interface GeminiAPIRequest {
  contents: Array<{
    parts: Array<{
      text: string;
    }>;
  }>;
  generationConfig: {
    temperature: number;
    topK: number;
    topP: number;
    maxOutputTokens: number;
  };
  safetySettings: Array<{
    category: string;
    threshold: string;
  }>;
}

export class GeminiClient {
  private readonly baseURL =
    "https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent";
  private readonly apiKey = import.meta.env.VITE_REACT_GEMINI_API_KEY;

  constructor() {
    if (!this.apiKey) {
      console.warn("Gemini API Key não configurada");
    }
  }

  protected async callGeminiAPI(prompt: string): Promise<string> {
    if (!this.apiKey) {
      throw new Error("Gemini API Key não configurada");
    }

    const request: GeminiAPIRequest = {
      contents: [
        {
          parts: [
            {
              text: prompt,
            },
          ],
        },
      ],
      generationConfig: {
        temperature: 0.3,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 32000, // Aumentado para maximum do Gemini 1.5 Pro
      },
      safetySettings: [
        {
          category: "HARM_CATEGORY_HARASSMENT",
          threshold: "BLOCK_MEDIUM_AND_ABOVE",
        },
        {
          category: "HARM_CATEGORY_HATE_SPEECH",
          threshold: "BLOCK_MEDIUM_AND_ABOVE",
        },
        {
          category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
          threshold: "BLOCK_MEDIUM_AND_ABOVE",
        },
        {
          category: "HARM_CATEGORY_DANGEROUS_CONTENT",
          threshold: "BLOCK_MEDIUM_AND_ABOVE",
        },
      ],
    };

    const response = await fetch(`${this.baseURL}?key=${this.apiKey}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: GeminiAPIResponse = await response.json();

    if (!data.candidates || data.candidates.length === 0) {
      throw new Error("Nenhuma resposta foi gerada pela IA");
    }

    return data.candidates[0].content.parts[0].text;
  }

  protected extractJsonFromResponse(response: string): any {
    try {
      const jsonMatch = response.match(/```json\n([\s\S]*?)\n```/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[1]);
      }

      const jsonStart = response.indexOf("{");
      const jsonEnd = response.lastIndexOf("}") + 1;
      if (jsonStart !== -1 && jsonEnd > jsonStart) {
        return JSON.parse(response.substring(jsonStart, jsonEnd));
      }

      return JSON.parse(response);
    } catch (error) {
      console.error("Erro ao extrair JSON da resposta:", error);
      throw new Error("Resposta da IA não está em formato JSON válido");
    }
  }
}